import type { TaxonomyNode } from '../api/types'

/** Flatten a category tree to depth-indented `<select>` options (parents first). */
export function flattenCategories(
  nodes: TaxonomyNode[],
  depth = 0,
): { id: string; label: string }[] {
  const out: { id: string; label: string }[] = []
  for (const n of nodes) {
    out.push({ id: n.node_id, label: `${'  '.repeat(depth)}${n.display_name}` })
    if (n.children?.length) out.push(...flattenCategories(n.children, depth + 1))
  }
  return out
}

/**
 * The set of category ids that match a selected category — the node itself plus
 * every descendant. Selecting a top-level category (e.g. "Clothing") therefore
 * matches listings tagged with any of its subcategories. Returns `null` when no
 * category is selected (i.e. no category filter).
 */
export function categoryMatchIds(
  nodes: TaxonomyNode[],
  selectedId: string | null,
): Set<string> | null {
  if (!selectedId) return null
  const node = findNode(nodes, selectedId)
  if (!node) return new Set([selectedId])
  const ids = new Set<string>()
  collect(node, ids)
  return ids
}

function findNode(nodes: TaxonomyNode[], id: string): TaxonomyNode | null {
  for (const n of nodes) {
    if (n.node_id === id) return n
    const hit = n.children?.length ? findNode(n.children, id) : null
    if (hit) return hit
  }
  return null
}

function collect(node: TaxonomyNode, into: Set<string>): void {
  into.add(node.node_id)
  for (const child of node.children ?? []) collect(child, into)
}
